import { useEffect, useState } from 'react'
import { useCurrentIo } from '../auth/useImpersonation'
import { loadProfile, saveProfile, type Profile } from './profile'

export type UseCreatorProfile = {
  profile: Profile | null
  creatorName: string
  profileLoaded: boolean
  profileSaving: boolean
  profileError: string | null
  needsProfileName: boolean
  saveCreatorName: (creatorName: string) => Promise<void>
}

export function useCreatorProfile(): UseCreatorProfile {
  const io = useCurrentIo()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [profileLoaded, setProfileLoaded] = useState(false)
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)

  // Reset when the active identity changes (logout, or an admin starting/stopping
  // impersonation) so the next render reloads a fresh profile for that identity.
  useEffect(() => {
    setProfile(null)
    setProfileLoaded(false)
    setProfileError(null)
  }, [io])

  useEffect(() => {
    if (!io || profileLoaded) return
    let cancelled = false
    loadProfile(io)
      .then((p) => {
        if (cancelled) return
        setProfile(p)
        setProfileLoaded(true)
      })
      .catch((err) => {
        if (cancelled) return
        const msg = err instanceof Error ? err.message : 'Failed to load profile'
        setProfileError(msg)
        setProfileLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [io, profileLoaded])

  const saveCreatorName = async (creatorName: string) => {
    if (!io) return
    setProfileSaving(true)
    setProfileError(null)
    const next: Profile = { ...(profile ?? {}), creatorName }
    try {
      await saveProfile(io, next)
      setProfile(next)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save profile'
      setProfileError(msg)
    } finally {
      setProfileSaving(false)
    }
  }

  const creatorName = profile?.creatorName?.trim() || ''
  const needsProfileName = profileLoaded && !creatorName

  return {
    profile,
    creatorName,
    profileLoaded,
    profileSaving,
    profileError,
    needsProfileName,
    saveCreatorName,
  }
}
